import { Injector, OnInit, OnDestroy, Directive, AfterContentInit } from '@angular/core';
import { ActivatedRoute, Router, Params, NavigationExtras } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { BaseModel } from 'src/app/shared/models/base.model';
import { FilterField } from 'src/app/shared/interfaces/filter.interface';
import { FindApiInterface } from 'src/app/shared/interfaces/find-api.interface';
import { GridColumn, GridOption } from 'src/app/shared/utils/grid';
import { SearchAction } from 'src/app/shared/interfaces/search.interface';
import { Status } from 'src/app/shared/interfaces/status.interface';

// Services
import { PermissionService } from 'src/app/shared/services/permission.service';
import { ResourceIdListService } from 'src/app/shared/services/resource-id-list.service';
import { RouterService } from 'src/app/shared/services/router.service';
import { ToastrMsgService } from 'src/app/shared/services/toastr-msg.service';
import { ChangePaginationEvent } from 'src/app/shared/components';
import { RenameCaptionService, PreviousRouteService } from 'src/app/shared/services';
import { PaginationData } from 'src/app/shared/components/pagination/pagination';
import { httpErrorMsgs } from 'src/app/core/utils/htp-error';

@Directive()
export abstract class BaseListComponent<T extends BaseModel> implements OnInit, OnDestroy, AfterContentInit {
  // @ViewChild('status') status: StatusComponent;

  // 96px é a altura do cabeçalho da página + o rodapé
  private rowHeight = 37.5;
  private mainHeight = document.getElementsByClassName('body')[0].clientHeight - 98;

  private subPreviousRoute: Subscription;

  private subRoute: Subscription;
  private subParamMap: Subscription;

  private previousRoute: PreviousRouteService;
  private searchFilter: string = null;

  protected defaultSearchMsg: string = 'Encontre o que deseja usando os filtros acima.';
  protected resourceIdField: string = 'id';
  protected defaultParams: Params = {};
  protected searchParams: Params = {};

  protected inModal: boolean = false;
  protected bsModalRef: BsModalRef;
  protected modalResolve: any;

  protected route: ActivatedRoute;
  protected router: Router;
  protected routerService: RouterService;
  protected previousUrl: string;
  protected subscriptions: Subscription[] = [];

  protected permissionService: PermissionService;
  protected toastrMsg: ToastrMsgService;
  protected resourceListService: ResourceIdListService;
  protected renameCaptionService: RenameCaptionService;

  public statusInfo: Status = null;

  public actions: SearchAction[] = [];
  public gridColumns: GridColumn[] = [];
  public gridOptions: GridOption[] = [];
  public resources: T[] = [];
  public filter: FilterField[] = [];
  public scrollTo: number;

  // Pagination
  public paginationData: PaginationData;
  public currentPage: number = 1;
  public itemsPerPage: number = Math.floor(this.mainHeight / this.rowHeight);
  public totalItemsSearch: number = 0;

  constructor(
    public title: string,
    protected apiService: FindApiInterface<T>,
    protected injector: Injector,
    public namespace?: string,
    public prefixAction?: string,
  ) {
    if (!this.namespace) {
      this.namespace = this.injector.get<string>('NS' as any, null);
    }

    if (!this.prefixAction) {
      this.prefixAction = this.injector.get<string>('AP' as any, null);
    }

    this.setPagination();
    this.loadServices();
  }

  ngOnInit() {
    this.title = this.renameCaptionService.getCaption(this.title);

    this.setDefaultColumns();
    this.setDefaultActions();
    this.setDefaultGridOptions();
  }

  ngAfterContentInit(): void {
    setTimeout(function () {
      this.loadRouteParams();
    }.bind(this), 500);
  }

  public ngOnDestroy(): void {
    if (this.subRoute) {
      this.subRoute.unsubscribe();
    }

    if (this.subParamMap) {
      this.subParamMap.unsubscribe();
    }

    if (this.subPreviousRoute) {
      this.subPreviousRoute.unsubscribe();
    }
  }

  private setPagination(): void {
    // Pagination
    this.paginationData = {
      itemsPerPage: Math.floor(this.mainHeight / this.rowHeight),
      totalItems: this.totalItemsSearch,
      currentPage: this.currentPage
    };
  }

  private errorHandler(err: any): void {
    if (err.status == 404) {
      this.setStatus({ error: 'Erro Interno! Recurso não encontrado.'});
    } else if (err.status !== 500) {
      const errors = httpErrorMsgs(err);
      errors.forEach(e => this.toastrMsg.error(e.msg, e.title));
      const error = errors[0] ? errors[0].msg : 'Erro ao carregar registros!';
      this.setStatus({ error });
    } else {
      this.setStatus({ error: 'Ocorreu um erro interno! Entre em contato com o suporte.'});
      console.log(err);
    }
  }

  protected loadServices() {
    this.route = this.injector.get(ActivatedRoute);
    this.router = this.injector.get(Router);
    this.toastrMsg = this.injector.get(ToastrMsgService);
    this.permissionService = this.injector.get(PermissionService);
    this.routerService = this.injector.get(RouterService);
    this.resourceListService = this.injector.get(ResourceIdListService);
    this.renameCaptionService = this.injector.get(RenameCaptionService);
    this.previousRoute = this.injector.get(PreviousRouteService);
    this.bsModalRef = this.injector.get(BsModalRef);
  }

  protected loadRouteParams() {
    if (this.inModal) {
      this.actionsSearch(this.defaultParams);
      return;
    }
    this.subRoute = this.route.queryParams.pipe(
      mergeMap(params => of({...this.defaultParams, ...params}))
    ).subscribe(params => {
      this.searchFilter = params['search'];

      this.filter.forEach(item => item.value = null);
      Object.keys(params).forEach(key => {
        const _filter = this.filter.find(item => item.field === key);
        const _value = params[key];
        if (_filter && _value != null) {
          _filter.value = _value;
        }
      });

      this.subParamMap = this.route.paramMap.subscribe(pMap => {
        this.searchParams = {...params, ...pMap['params']};

        if (Object.keys(this.searchParams).length > 0) {
          this.search({...this.defaultParams, ...this.searchParams });
        } else {
          this.resources = [];
          this.totalItemsSearch = 0;
          this.setStatus({ info: this.defaultSearchMsg });
        }
      });
    });

    this.subPreviousRoute = this.previousRoute.previousUrl.subscribe(res => this.previousUrl = res);
  }

  private getPaginationParams() {
    return {
      limit: this.itemsPerPage + '',
      offset: ((this.itemsPerPage * this.currentPage) - this.itemsPerPage) + ''
    };
  }

  private setCurrentPage(params: Params, defValue: number = 1): boolean {
    // Verifica se existe paginação e posiciona na página correta
    const offset = params['offset'];
    const limit = params['limit'];
    let newPage = 0;
    if (!!offset && !!limit && +offset > 0) {
      newPage = (+offset / +limit) + 1;
    } else {
      newPage = defValue;
    }

    if (newPage !== this.currentPage) {
      this.currentPage = newPage;
      return true;
    }
    return false;
  }

  search(params: Params): void {
    this.setStatus({ info: 'Carregando Registros...', load: true });

    let paginationParmams = {};
    const limitParam = params['limit'];
    if (!limitParam) {
      paginationParmams = this.getPaginationParams();
    }

    const searchParams = { ...params, ...paginationParmams };

    this.apiService.formSearch(searchParams).subscribe(result => {
      if (this.totalItemsSearch !== result.count) {
        this.totalItemsSearch = result.count;
      }

      // this.setCurrentPage(this.searchParams);
      this.setCurrentPage(searchParams);
      this.setPagination();

      this.resources = [];
      result.results.forEach(item => this.resources.push(item));

      const ids = this.resources.map(item => item.id);
      this.resourceListService.setResourceIdList(ids);

      this.hideStatus();
      this.afterFind(this.resources);
    }, err => this.errorHandler(err));
  }

  changePage(event: ChangePaginationEvent): void {
    const params = this.filterToParms(this.filter);

    if (this.searchFilter) {
      params['search'] = this.searchFilter;
    }

    if (this.currentPage !== event.page) {
      this.currentPage = event.page;
    }

    this.actionsSearch({...this.defaultParams, ...this.searchParams, ...params});
  }

  doNavigate(commands: any[], extras?: NavigationExtras): Promise<boolean> {
    if (this.inModal) {
      return of(false).toPromise();
    }
    return this.router.navigate(commands, extras);
  }

  actionsSearch(params: Params): void {
    const searchParams = {};

    if (this.searchFilter !== params['search']) {
      this.currentPage = 1;
      this.searchFilter = params['search'];
    }

    // Filtra apenas parâmetros com valor
    delete params['limit'];
    delete params['offset'];
    const bsParam = this.beforeSearch({...this.getPaginationParams(), ...params});
    Object.keys(bsParam).forEach(k => {
      if (!!bsParam[k]) {
        searchParams[k] = bsParam[k];
      }
    });

    this.doNavigate(['.'], { relativeTo: this.route, queryParams: searchParams })
      .then((navigated) => {
        // Se a rota é a mesma, força atualização dos registros
        if (!navigated) {
          this.search({...this.defaultParams, ...searchParams});
        } else {
          this.setCurrentPage(searchParams, 1);
        }
      });
  }

  protected beforeSearch(params: Params): Params {
    // Caso exista parâmetros padrões não houver parâmetro
    // if (this.defaultParams && params && Object.keys(params).length === 0) {
    //   params = this.defaultParams;
    // }

    // params = Object.assign(params, this.defaultParams);

    // Configura parâmetros padrões
    Object.keys(this.defaultParams).forEach(key => {
      // Se não houver parâmetros padrões no filtro, adiciona
      if (!params[key]) {
        params[key] = this.defaultParams[key];
      }
    });

    if (!params['search'] && !!this.searchFilter) {
      params['search'] = this.searchFilter;
    }

    // Pode ser implementado e manipulado nos filhos
    return params;
  }

  protected filterToParms(filter: FilterField[]): Params {
    filter = filter ? filter : this.filter;
    const params = {};
    filter.filter(a => a.value).forEach(b => params[b.field] = b.value);
    return params;
  }

  protected refreshSearch(filter: FilterField[]): void {
    const params = this.filterToParms(filter);
    params['search'] = this.searchFilter;
    this.actionsSearch(params);
  }

  protected afterFind(data: T[]): void {
    // Pode ser implementado nos filhos
  }

  createGridColumns(config: GridColumn[]): void {
    config.forEach(item => {
      const exist = this.gridColumns.filter(a => a.field === item.field);

      if (exist.length === 0) {
        const column: GridColumn = {
          field: item.field,
          title: item.title,
          icon: item.icon,

          clickEvent: item.clickEvent ? item.clickEvent.bind(this) : null,
          cls: item.cls,
          getCls: item.getCls,
          complement: item.complement,
          format: item.format,
          style: item.style,
          type: item.type,
          valueCallback: item.valueCallback ? item.valueCallback.bind(this) : null,
        };

        this.gridColumns.push(column);
      }

    });
  }

  createGridOptions(config: GridOption[]) {
    config.forEach(item => {
      let hasPerm = true;
      if (!!item.namespace) {
        hasPerm = this.permissionService.checkPermission(item.namespace, item.action);
      }

      if (hasPerm) {
        const exist = this.gridOptions.filter(a => a.icon === item.icon && a.title === item.title);
        if (exist.length === 0) {
          this.gridOptions.push(new GridOption(item.icon, item.title, item.clickEvent, null, null, true, item.condition));
        }
      }
    });
  }

  protected setStatus(status: Status): void {
    this.statusInfo = status;
    // if (this.status) {
    //   this.status.setStatus(status);
    // }
  }

  protected hideStatus(): void {
    this.statusInfo = null;
    // if (this.status) {
    //   this.status.hide();
    // }
  }

  protected setDefaultColumns(): void {
    this.createGridColumns([
      { title: '', field: '', cls: 'text-center', type: 'options' }
    ]);
  }

  protected setDefaultActions(): void {
    this.actions = [{
      name: 'add', icon: 'fa fa-plus', title: 'Novo Item',
      permission: [this.namespace, `${this.prefixAction}_create`]
    }, ...this.actions];
  }

  protected setDefaultGridOptions(): void {
    this.createGridOptions([{
      icon: 'far fa-folder-open icon', title: 'Abrir', clickEvent: this.detail.bind(this),
      namespace: this.namespace, action: `${this.prefixAction}_view`
    }]);
  }

  public getItemValue(item: T, field: string): any {
    let value = item;
    field.split('.').forEach(f => value = value[f]);
    return value;
  }

  protected getResourceById(id: number): T | null {
    return this.resources.find(r => r.id === id);
  }

  protected updateResource(resource: T): boolean {
    const current = this.getResourceById(resource.id);
    const idx = this.resources.indexOf(current);
    this.resources[idx] = resource;
    return false;
  }

  public detail(event: Event, item: T): void {
    if (this.inModal) {
      this.modalResolve(item);
      this.bsModalRef.hide();
    } else {
      const resourceId = item[this.resourceIdField];
      this.detailById(event, resourceId);
    }
  }

  public detailById(event: Event, resourceId: number): void {
    this.routerService.navigate(this.namespace, `${this.prefixAction}_view`, [resourceId],
      { relativeTo: this.route, queryParamsHandling: 'merge' });
  }

  public actionsClick(actionName: string): void {
    switch (actionName) {
      case 'add': {
        this.routerService.navigate(this.namespace, `${this.prefixAction}_create`, ['new'],
          { relativeTo: this.route, queryParamsHandling: 'merge' });
        break;
      }
      case 'close': {
        if (this.inModal) {
          this.bsModalRef.hide();
        }
        break;
      }
    }
  }
}
